import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { PaginationSettings } from "@/Common/Models/PaginationSettings";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { BillingPaymentMethod } from "@/Common/Models/UI/BillingPaymentMethod";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { BillingInbox, PayableInboxSortOrderType, PayableInboxValidity } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";
import { FacturationNetResponseError } from "../Api/FacturationNet/FacturationNetResponseError";
import { BillingHelper } from "../Helpers/BillingHelper";
import { PayableInboxFilterSettings } from "../Models/Settings/PayableInboxFilterSettings";
import { BillingClaimStatusInboxModel } from "../Models/UI/BillingClaimStatusInboxModel";
import { IBillingPopupModel } from "../Models/UI/IBillingPopupModel";
import { IBillingTabModel } from "../Models/UI/IBillingTabModel";
import { ISalesInboxModel } from "../Models/UI/ISalesInboxModel";
import { ITransactionsInboxModel } from "../Models/UI/ITransactionsInboxModel";
import { PayableInboxValidityUIModel } from "../Models/UI/PayableInboxValidityUIModel";
import { PayableInboxModel } from "../Models/UI/PaybleInboxModel";
import { BillingAction } from "./Actions/BillingAction";
import { BillingClaimStatusInboxHandler } from "./Handlers/Local/BillingClaimStatusInboxHandler";
import { BillingTabHandler } from "./Handlers/Local/BillingTabHandler";
import { PayableInboxHandler } from "./Handlers/Local/PayableInboxHandler";
import { SalesInboxHandler } from "./Handlers/Local/SalesInboxHandler";
import { SalesSettingsContainerHandler } from "./Handlers/Local/SalesSettingsContainerHandler";
import { TransactionsInboxHandler } from "./Handlers/Local/TransactionsInboxHandler";
import { TransactionsSettingsContainerHandler } from "./Handlers/Local/TransactionsSettingsContainerHandler";
import { BillingResponseHandler } from "./Handlers/Server/BillingResponseHandler";

let defaultInstance: BillingStore;

class BillingStore extends BaseStore {
    private mapper: RequestMapper;
    private serverMapper: ServerResponseMapper;
    private serverResponseHandler: BillingResponseHandler;

    private billingTabHandler: BillingTabHandler;
    private transactionsInboxHandler: TransactionsInboxHandler;
    private salesInboxHandler: SalesInboxHandler;
    private transactionsSettingsContainerHandler: TransactionsSettingsContainerHandler;
    private salesSettingsContainerHandler: SalesSettingsContainerHandler;
    private payableInboxHandler: PayableInboxHandler;
    private billingClaimStatusInboxHandler: BillingClaimStatusInboxHandler;

    public Model: IBillingTabModel;
    public TransactionsInboxModel: ITransactionsInboxModel;
    public SalesInboxModel: ISalesInboxModel;
    public payableInboxModel: PayableInboxModel;
    public BillingPopupModel: IBillingPopupModel;
    public BillingClaimStatusInboxModel: BillingClaimStatusInboxModel;

    public BillingAction: BillingAction;

    public helper: MyleHelper;

    constructor() {
        super();

        globalServerResponseHandler.addErrorHandler(new FacturationNetResponseError());

        this.helper = new MyleHelper();
        var self = this;

        this.BillingAction = new BillingAction();

        this.mapper = new RequestMapper();
        this.serverMapper = new ServerResponseMapper();

        this.billingTabHandler = new BillingTabHandler();
        this.transactionsInboxHandler = new TransactionsInboxHandler();
        this.salesInboxHandler = new SalesInboxHandler();
        this.payableInboxHandler = new PayableInboxHandler();
        this.billingClaimStatusInboxHandler = new BillingClaimStatusInboxHandler();
        this.transactionsSettingsContainerHandler = new TransactionsSettingsContainerHandler();
        this.salesSettingsContainerHandler = new SalesSettingsContainerHandler();
        this.serverResponseHandler = new BillingResponseHandler(this.BillingAction);

        this.mapper.AddInstance(this.billingTabHandler);
        this.mapper.AddInstance(this.transactionsInboxHandler);
        this.mapper.AddInstance(this.salesInboxHandler);
        this.mapper.AddInstance(this.transactionsSettingsContainerHandler);
        this.mapper.AddInstance(this.salesSettingsContainerHandler);
        this.mapper.AddInstance(this.payableInboxHandler);
        this.mapper.AddInstance(this.billingClaimStatusInboxHandler);

        this.serverMapper.AddInstance(this.serverResponseHandler);

        let defaultModel = { CurrentInbox: BillingInbox.Transactions };
        if (cacheStore.ClinicSettings.EnableBillingReconciliationReport
            && !sessionStore.hasPermission(EnumMyleLoginPermission.PrivateBillingPermission)
            && sessionStore.hasPermission(EnumMyleLoginPermission.PublicBillingPermission)) {
            defaultModel = { CurrentInbox: BillingInbox.BillingClaimStatus };
        }

        const defaultTransactionsInboxModel = BillingHelper.getDefaultTransactionInboxModel();

        const defaultSalesInboxModel = BillingHelper.GetDefaultSalesInboxModel();

        const defaultBillingPopupModel = {
            SiteName: ""
        };

        this.Model = Immutable.fromJS(defaultModel);
        this.TransactionsInboxModel = Immutable.fromJS(defaultTransactionsInboxModel);
        this.SalesInboxModel = Immutable.fromJS(defaultSalesInboxModel);
        this.BillingPopupModel = Immutable.fromJS(defaultBillingPopupModel);
        this.BillingClaimStatusInboxModel = BillingClaimStatusInboxModel.fromCacheModel(cacheStore.UserSettings.BillingClaimStatusReportSettings) ?? BillingClaimStatusInboxModel.getDefaultFilterSettings();

        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => { self.serverMapper.Map(response, self); });
        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => self.mapper.Map(request, self));

        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient)
            this.initializePayableInboxFilterSettings();
    }

    public static createSingleton(): BillingStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new BillingStore());
    }

    public static createSingletonFrom(store: BillingStore, copy: boolean = true): BillingStore {
        return copy
            ? Object.assign(this.createSingleton(), store)
            : this.createStoreSingleton(defaultInstance, () => defaultInstance = store);
    }

    public loadTransactionsProviderUICache(): void {
        let seeAllClinicians = sessionStore.hasPermission(EnumMyleLoginPermission.BillingTabPermission);

        var list: EmployeeUIModel[] = [];
        if (seeAllClinicians) {
            for (let composite of cacheStore.EmployeeCacheWithInactive) {
                var employee = EmployeeUIModel.fromCompositeObject(composite);
                employee.Target = CheckboxTarget.Myle_Billing_Transactions_Provider;
                list.push(employee);
            }
        }
        else {
            var employee = sessionStore.getCurrentEmployee();
            employee.Target = CheckboxTarget.Myle_Billing_Transactions_Provider;
            list.push(employee);
        }

        this.TransactionsInboxModel = this.TransactionsInboxModel.set("ProviderGlobalCache", Immutable.List(list));
        this.TransactionsInboxModel = this.TransactionsInboxModel.set("ProviderFiltered", this.getTransactionsProviderBasedOnSitesAndQuery());
    }
    public getTransactionsProviderBasedOnSitesAndQuery(): Immutable.List<EmployeeUIModel> {
        var cliniciansFromSites = this.getTransactionsProviderFromSelectedSites();
        var filterString = this.TransactionsInboxModel.get("ProviderSearchText").trim();
        if (filterString !== "") { cliniciansFromSites = SearchMenuHelper.FilterCliniciansByName(filterString, cliniciansFromSites); }
        return cliniciansFromSites;
    }

    public getTransactionsProviderFromSelectedSites(): Immutable.List<EmployeeUIModel> {
        var checkedSiteIds = this.helper.GetCheckedItemsFromICheckableList(this.TransactionsInboxModel.get("Sites")).map((site: SiteUIModel) => site.Id).toArray();
        var allClinicians = this.TransactionsInboxModel.get("ProviderGlobalCache");
        return SearchMenuHelper.FilterCliniciansMembersOfSites(allClinicians, checkedSiteIds);
    }

    public loadTransactionsBilledByClinicianUICache(): void {
        var list: EmployeeUIModel[] = [];

        for (let composite of cacheStore.EmployeeCacheWithInactive) {
            var employee = EmployeeUIModel.fromCompositeObject(composite);
            employee.Target = CheckboxTarget.Myle_Billing_Transactions_BilledBy;
            list.push(employee);
        }

        this.TransactionsInboxModel = this.TransactionsInboxModel.set("BilledByGlobalCache", Immutable.List(list));
        this.TransactionsInboxModel = this.TransactionsInboxModel.set("BilledByFiltered", this.getTransactionsBilledByBasedOnSitesAndQuery());
    }

    public getTransactionsBilledByBasedOnSitesAndQuery(): Immutable.List<EmployeeUIModel> {
        var cliniciansFromSites = this.getTransactionsBilledByFromSelectedSites();
        var filterString = this.TransactionsInboxModel.get("BilledBySearchText").trim();
        if (filterString !== "") { cliniciansFromSites = SearchMenuHelper.FilterCliniciansByName(filterString, cliniciansFromSites); }
        return cliniciansFromSites;
    }

    public getTransactionsBilledByFromSelectedSites(): Immutable.List<EmployeeUIModel> {
        var checkedSites = this.helper.GetCheckedItemsFromICheckableList(this.TransactionsInboxModel.get("Sites"));

        var allClinicians = this.TransactionsInboxModel.get("BilledByGlobalCache");

        var result: EmployeeUIModel[] = [];
        for (var i = 0; i < checkedSites.size; i++) {
            for (var j = 0; j < allClinicians.size; j++) {
                var clinician = allClinicians.get(j);
                var sites = clinician.Sites;
                var hasSite = sites.filter(item => item === checkedSites.get(i).Id).length > 0;
                if (hasSite) {
                    var toAdd = result.filter(i => i.Id === clinician.Id).length === 0;
                    if (toAdd) { result.push(clinician); } // prevent duplicates due to many-to-many relationship between clinicians and sites
                }
            }
        }
        return Immutable.List(result);
    }

    // SALES EMPLOYEE/SITE/PAYMENT METHOD/STATUS LIST FOR SETTINGS -----------------------------------------

    public loadSalesProviderUICache(): void {
        let seeAllClinicians = sessionStore.hasPermission(EnumMyleLoginPermission.BillingTabPermission);

        var list: EmployeeUIModel[] = [];
        if (seeAllClinicians) {
            for (let composite of cacheStore.EmployeeCacheWithInactive) {
                var employee = EmployeeUIModel.fromCompositeObject(composite);
                employee.Target = CheckboxTarget.Myle_Billing_Sales_Provider;
                list.push(employee);
            }
        }
        else {
            var employee = sessionStore.getCurrentEmployee();
            employee.Target = CheckboxTarget.Myle_Billing_Sales_Provider;
            list.push(employee);
        }

        this.SalesInboxModel = this.SalesInboxModel.set("ProviderGlobalCache", Immutable.List(list));
        this.SalesInboxModel = this.SalesInboxModel.set("ProviderFiltered", this.getSalesProviderBasedOnSitesAndQuery());
    }

    public getSalesProviderBasedOnSitesAndQuery(): Immutable.List<EmployeeUIModel> {
        var cliniciansFromSites = this.getSalesProviderFromSelectedSites();
        var filterString = this.SalesInboxModel.get("ProviderSearchText").trim();
        if (filterString !== "") { cliniciansFromSites = SearchMenuHelper.FilterCliniciansByName(filterString, cliniciansFromSites); }
        return cliniciansFromSites;
    }

    public getSalesProviderFromSelectedSites(): Immutable.List<EmployeeUIModel> {
        var checkedSites = this.helper.GetCheckedItemsFromICheckableList(this.SalesInboxModel.get("Sites"));

        var allClinicians = this.SalesInboxModel.get("ProviderGlobalCache");

        var result: EmployeeUIModel[] = [];
        for (var i = 0; i < checkedSites.size; i++) {
            for (var j = 0; j < allClinicians.size; j++) {
                var clinician = allClinicians.get(j);
                var sites = clinician.Sites;
                var hasSite = sites.filter(item => item === checkedSites.get(i).Id).length > 0;
                if (hasSite) {
                    var toAdd = result.filter(i => i.Id === clinician.Id).length === 0;
                    if (toAdd) { result.push(clinician); } // prevent duplicates due to many-to-many relationship between clinicians and sites
                }
            }
        }
        return Immutable.List(result);
    }

    public loadSalesBilledByClinicianUICache(): void {
        var list: EmployeeUIModel[] = [];
        for (let composite of cacheStore.EmployeeCacheWithInactive) {
            var employee = EmployeeUIModel.fromCompositeObject(composite);
            employee.Target = CheckboxTarget.Myle_Billing_Sales_BilledBy;
            list.push(employee);
        }

        this.SalesInboxModel = this.SalesInboxModel.set("BilledByGlobalCache", Immutable.List(list));
        this.SalesInboxModel = this.SalesInboxModel.set("BilledByFiltered", this.getSalesBilledByBasedOnSitesAndQuery());
    }

    public getSalesBilledByBasedOnSitesAndQuery(): Immutable.List<EmployeeUIModel> {
        var cliniciansFromSites = this.getSalesBilledByFromSelectedSites();
        var filterString = this.SalesInboxModel.get("BilledBySearchText").trim();
        if (filterString !== "") { cliniciansFromSites = SearchMenuHelper.FilterCliniciansByName(filterString, cliniciansFromSites); }
        return cliniciansFromSites;
    }

    public getSalesBilledByFromSelectedSites(): Immutable.List<EmployeeUIModel> {
        var checkedSites = this.helper.GetCheckedItemsFromICheckableList(this.SalesInboxModel.get("Sites"));

        var allClinicians = this.SalesInboxModel.get("BilledByGlobalCache");

        var result: EmployeeUIModel[] = [];
        for (var i = 0; i < checkedSites.size; i++) {
            for (var j = 0; j < allClinicians.size; j++) {
                var clinician = allClinicians.get(j);
                var sites = clinician.Sites;
                var hasSite = sites.filter(item => item === checkedSites.get(i).Id).length > 0;
                if (hasSite) {
                    var toAdd = result.filter(i => i.Id === clinician.Id).length === 0;
                    if (toAdd) { result.push(clinician); } // prevent duplicates due to many-to-many relationship between clinicians and sites
                }
            }
        }
        return Immutable.List(result);
    }

    public initializeBillingStatus(operation: CheckboxTarget): Immutable.List<CheckboxUIModel> {
        var open = new CheckboxUIModel();
        open.Id = "open_status";
        open.Name = cacheStore.GetString("IsOpenLabel");
        open.Target = operation;
        open.Checked = false;

        var closed = new CheckboxUIModel();
        closed.Id = "closed_status";
        closed.Name = cacheStore.GetString("ClosedDLabel");
        closed.Target = operation;
        closed.Checked = true;

        return Immutable.List([closed, open]);
    }

    public initializeBillingValidity(operation: CheckboxTarget): Immutable.List<CheckboxUIModel> {
        var valid = new CheckboxUIModel();
        valid.Id = "valid";
        valid.Name = cacheStore.GetString("ValidLabel");
        valid.Target = operation;
        valid.Checked = true;

        var invalid = new CheckboxUIModel();
        invalid.Id = "invalid";
        invalid.Name = cacheStore.GetString("InvalidLabel");
        invalid.Target = operation;
        invalid.Checked = false;

        return Immutable.List([invalid, valid]);
    }

    public initializeBillingPaymentMethod(operation: CheckboxTarget): Immutable.List<CheckboxUIModel> {
        let paymentMethods = Immutable.List<CheckboxUIModel>();

        const cash = new CheckboxUIModel();
        cash.Id = BillingPaymentMethod.Cash.toString();
        cash.Name = cacheStore.GetString("CashLabel");
        cash.Target = operation;
        cash.Checked = true;
        paymentMethods = paymentMethods.push(cash);

        const debit = new CheckboxUIModel();
        debit.Id = BillingPaymentMethod.DebitCard.toString();
        debit.Name = cacheStore.GetString("DebitCardLabel");
        debit.Target = operation;
        debit.Checked = true;
        paymentMethods = paymentMethods.push(debit);

        const visa = new CheckboxUIModel();
        visa.Id = BillingPaymentMethod.Visa.toString();
        visa.Name = cacheStore.GetString("VisaLabel");
        visa.Target = operation;
        visa.Checked = true;
        paymentMethods = paymentMethods.push(visa);

        const check = new CheckboxUIModel();
        check.Id = BillingPaymentMethod.Check.toString();
        check.Name = cacheStore.GetString("CheckLabel");
        check.Target = operation;
        check.Checked = true;
        paymentMethods = paymentMethods.push(check);

        const amExpress = new CheckboxUIModel();
        amExpress.Id = BillingPaymentMethod.AmericanExpress.toString();
        amExpress.Name = cacheStore.GetString("AmericanExpressLabel");
        amExpress.Target = operation;
        amExpress.Checked = true;
        paymentMethods = paymentMethods.push(amExpress);

        const masCard = new CheckboxUIModel();
        masCard.Id = BillingPaymentMethod.MasterCard.toString();
        masCard.Name = cacheStore.GetString("MasterCardLabel");
        masCard.Target = operation;
        masCard.Checked = true;
        paymentMethods = paymentMethods.push(masCard);

        const onPay = new CheckboxUIModel();
        onPay.Id = BillingPaymentMethod.OnlinePayment.toString();
        onPay.Name = cacheStore.GetString("OnlinePaymentLabel");
        onPay.Target = operation;
        onPay.Checked = true;
        paymentMethods = paymentMethods.push(onPay);

        const payable = new CheckboxUIModel();
        payable.Id = BillingPaymentMethod.Payable.toString();
        payable.Name = cacheStore.GetString("PayableLabel");
        payable.Target = operation;
        payable.Checked = true;
        paymentMethods = paymentMethods.push(payable);

        if (!cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
            const receivable = new CheckboxUIModel();
            receivable.Id = BillingPaymentMethod.Receivable.toString();
            receivable.Name = cacheStore.GetString("ReceivableLabel");
            receivable.Target = operation;
            receivable.Checked = true;
            paymentMethods = paymentMethods.push(receivable);
        }
        else {
            const other = new CheckboxUIModel();
            other.Id = BillingPaymentMethod.Other.toString();
            other.Name = cacheStore.GetString("OtherLabel");
            other.Target = operation;
            other.Checked = true;
            paymentMethods = paymentMethods.push(other);
        }

        const etf = new CheckboxUIModel();
        etf.Id = BillingPaymentMethod.ElectronicFunds.toString();
        etf.Name = cacheStore.GetString("ElectronicFundsTransferLabel");
        etf.Target = operation;
        etf.Checked = true;
        paymentMethods = paymentMethods.push(etf);

        return paymentMethods;
    }

    private initializePayableInboxFilterSettings(): void {
        const model = { ...this.payableInboxModel };
        const availableClinicians = ArrayHelper.SortDynamic(cacheStore.GetCliniciansAsEmployees(CheckboxTarget.Unassigned, true)
            .filter(p => p.Model.UserName !== "radpdf_api"), "Name");

        model.availableSites = ArrayHelper.SortDynamic(cacheStore.GetSites(CheckboxTarget.Unassigned), "Name");
        model.availableProviders = sessionStore.hasPermission(EnumMyleLoginPermission.BillingTabPermission)
            ? availableClinicians
            : [sessionStore.getCurrentEmployee()];
        model.availableBilledBy = availableClinicians;
        model.availableValidity = this.getPayableInboxValidities();

        model.filterSettings = cacheStore.UserSettings.PayableInboxFilterSettings ?? this.getDefaultPayableInboxFilterSettings(model);
        model.filterSettings.Pagination = new PaginationSettings<PayableInboxSortOrderType>(PayableInboxSortOrderType.BillDate);

        this.payableInboxModel = model;
    }

    private getPayableInboxValidities(): PayableInboxValidityUIModel[] {
        return Object.values(PayableInboxValidity)
            .filter(value => !isNaN(Number(value)))
            .map(status => new PayableInboxValidityUIModel(status));
    }

    private getDefaultPayableInboxFilterSettings(model: PayableInboxModel): PayableInboxFilterSettings {
        const defaultFilterSettings = new PayableInboxFilterSettings();

        defaultFilterSettings.SiteIds = model.availableSites.map(site => site.Id);
        defaultFilterSettings.CliniciansProviderContactFacetIds = model.availableProviders.map(clinician => clinician.ContactFacetId);
        defaultFilterSettings.CliniciansBilledByContactFacetIds = model.availableBilledBy.map(clinician => clinician.ContactFacetId);
        defaultFilterSettings.ValidityEnums = model.availableValidity.map(validity => validity.validity);

        defaultFilterSettings.Pagination.PageSize = 50;
        return defaultFilterSettings;
    }
}

export { BillingStore, defaultInstance as billingStore };
